// DevSubscriptionPanel — v0.8.0 (Dev / Mock Stripe)
//
// Floating dev panel — only rendered when REACT_APP_STRIPE_MOCK=true.
// Shows the current subscription state and has one-click buttons to
// jump to any state, so you can test every paywall scenario without
// navigating through the mock checkout flow.
//
// Collapse button keeps it out of the way when not needed.
// Never shown in production (env var is undefined → component returns null).

import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useSubscription } from '../context/SubscriptionContext';
import { API_BASE_URL } from '../services/api';

const IS_MOCK = process.env.REACT_APP_STRIPE_MOCK === 'true';

// Keep in sync with mock-stripe-reset.js STATES object. Both must list
// the same keys. (mock-stripe-reset.js also keeps a legacy 'active' alias
// for external callers — that one is intentionally NOT cycled here since
// the panel ships the explicit active_investor button instead.)
const STATES = [
  { key: 'trialing',            label: '⏳ Trial (7 days)',       description: 'Full access · Free Trial',        color: 'var(--color-tier-trial)' },
  { key: 'trial_expiring',      label: '⚠️ Trial (1 day left)',   description: 'Banner appears',                  color: 'var(--color-warning-lt)' },
  { key: 'trial_expired',       label: '🔒 Trial Expired',        description: 'Paywall blocks access',           color: 'var(--color-danger-lt)' },
  { key: 'active_investor',     label: '✓ Active · Investor',     description: 'Full access · Investor tier',     color: 'var(--color-tier-investor)' },
  { key: 'active_professional', label: '✓ Active · Professional', description: 'Full access · Professional tier', color: 'var(--color-tier-pro)' },
  { key: 'active_enterprise',   label: '✓ Active · Enterprise',   description: 'Full access · Enterprise tier',   color: 'var(--color-tier-enterprise)' },
  { key: 'canceled',            label: '✕ Canceled',              description: 'Subscription ended',              color: 'var(--color-steel)' },
];

async function setState(state) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return;

  await fetch(`${API_BASE_URL}/mock-stripe-reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ state }),
  });
}

export default function DevSubscriptionPanel() {
  const { status, tier, trialDaysLeft, isInvestor, isProfessional, isEnterprise, isTrialing, isExpired } = useSubscription();
  const [collapsed, setCollapsed] = useState(false);
  const [busy, setBusy]           = useState(null);

  if (!IS_MOCK) return null;

  // v1.2.0 — show the actual paid tier instead of the generic "Investor Active"
  // label that assumed Investor was the only paid tier. Falls through to status
  // for unknown states (legacy 'pro' rows from pre-v1.2.0 mock testing, etc).
  const currentState = isInvestor
    ? 'Active · Investor'
    : isProfessional
    ? 'Active · Professional'
    : isEnterprise
    ? 'Active · Enterprise'
    : isTrialing
    ? `Trialing (${trialDaysLeft}d left)`
    : isExpired
    ? 'Expired'
    : tier && tier !== 'free'
    ? `${tier} (${status})`
    : status;

  async function handleSet(stateKey) {
    setBusy(stateKey);
    await setState(stateKey);
    // Give Supabase a beat then reload the page so SubscriptionContext re-fetches.
    setTimeout(() => window.location.reload(), 400);
  }

  return (
    <div style={panelStyle}>
      <div style={headerStyle} onClick={() => setCollapsed((c) => !c)}>
        <span style={titleStyle}>🧪 Dev Billing</span>
        <span style={{ color: '#9ca3af', fontSize: '11px' }}>{collapsed ? '▲ expand' : '▼ collapse'}</span>
      </div>

      {!collapsed && (
        <>
          <div style={stateRowStyle}>
            <span style={stateLabelStyle}>Current:</span>
            <span style={stateValueStyle}>{currentState}</span>
          </div>

          <div style={buttonGroupStyle}>
            {STATES.map(({ key, label, description, color }) => (
              <button
                key={key}
                style={stateButtonStyle(color, busy === key)}
                onClick={() => handleSet(key)}
                disabled={!!busy}
              >
                {busy === key ? '…' : (
                  <>
                    <div>{label}</div>
                    <div style={stateDescriptionStyle}>{description}</div>
                  </>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const panelStyle = {
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  background: '#1f2937',
  color: '#f9fafb',
  borderRadius: '12px',
  padding: '12px 14px',
  zIndex: 9999,
  minWidth: '200px',
  maxWidth: '240px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.40)',
  fontSize: '12px',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  marginBottom: '8px',
};

const titleStyle = {
  fontWeight: 700,
  fontSize: '12px',
  color: '#f9fafb',
};

const stateRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '10px',
  padding: '6px 8px',
  background: '#374151',
  borderRadius: '6px',
};

const stateLabelStyle = {
  color: '#9ca3af',
};

const stateValueStyle = {
  color: '#f9fafb',
  fontWeight: 600,
};

const buttonGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
};

const stateButtonStyle = (color, busy) => ({
  background: busy ? '#374151' : 'transparent',
  border: `1px solid ${color}`,
  borderRadius: '6px',
  padding: '5px 8px',
  color: busy ? '#9ca3af' : color,
  fontSize: '11px',
  fontWeight: 600,
  cursor: busy ? 'not-allowed' : 'pointer',
  textAlign: 'left',
});

// Description line below each state button — small muted text so it doesn't
// crowd the label but makes the test scenario clear at a glance.
const stateDescriptionStyle = {
  fontSize: '10px',
  fontWeight: 400,
  color: 'var(--color-steel-lt)',
  marginTop: '2px',
  opacity: 0.85,
};
